import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Language, Theme, MenuItem } from './types';
import { findDishByImage } from './services/geminiService';
import MenuSection from './components/MenuSection';
import CategoryNav from './components/CategoryNav';
import { smoothScrollTo } from './src/utils/smoothScroll';
import AiRecommender from './components/AiRecommender';
import Welcome from './components/Welcome';
import DishDetailModal from './components/DishDetailModal';
import { ValleyOfTasteLogo } from './components/icons';
import NoResults from './components/NoResults';
import { t } from './translations';
import { useTheme } from './contexts/ThemeContext';
import { useMenu } from './src/contexts/MenuContext';
import { useSettings } from './src/contexts/SettingsContext';
import BottomDock from './components/BottomDock';
import SearchHeader from './components/SearchHeader';
import CartWidget from './components/CartWidget';
import FeaturedDishes from './components/FeaturedDishes';
import RecentlyViewed from './components/RecentlyViewed';
import SkeletonLoading from './components/SkeletonLoading';
import Footer from './components/Footer';

import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useLocation } from 'react-router-dom';

import { useLanguage } from './contexts/LanguageContext';
import { useUser } from './contexts/UserContext';
import { useScrollLock } from './src/utils/useScrollLock';
import { toast } from 'sonner';
import { trackFunnel } from './src/utils/funnel';
import { useFavourites } from './src/utils/useFavourites';
import { tableLabel } from './src/utils/tableLabel';

const Home: React.FC = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const location = useLocation();
  // Режим публичного самовывоза — когда зашли на /takeaway без стола
  const takeawayMode = location.pathname === '/takeaway';
  const { user } = useUser();
  const { theme, setTheme, toggleTheme } = useTheme();
  const { menuData, loading: menuLoading } = useMenu();
  const { settings } = useSettings();
  const { language, setLanguage } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [recommendedItemId, setRecommendedItemId] = useState<number | null>(null);
  
  const [selectedDish, setSelectedDish] = useState<MenuItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const { favs } = useFavourites();
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const mainScrollRef = useRef<HTMLElement>(null);
  const isScrollingRef = useRef(false);
  const recommendationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (recommendationTimerRef.current) clearTimeout(recommendationTimerRef.current);
      if (scrollResetTimerRef.current) clearTimeout(scrollResetTimerRef.current);
    };
  }, []);

  // Set table number in localStorage if present in URL
  useEffect(() => {
    if (tableId && user) {
      localStorage.setItem(`table_number_${user.uid}`, tableId);
    }
  }, [tableId, user]);

  // Conversion funnel: открытие меню. Дедуп — 1 раз в сутки per uid.
  useEffect(() => {
    if (!user) return;
    trackFunnel('menu_open', { uid: user.uid, dedupePerDay: true });
  }, [user]);

  // В takeaway-режиме стола нет. В остальных случаях — из URL или из localStorage.
  const currentTable = takeawayMode
    ? ''
    : tableId || (user ? localStorage.getItem(`table_number_${user.uid}`) : null) || '';

  useEffect(() => {
    if (menuData.length > 0 && !activeCategory) {
      setActiveCategory(menuData[0].id);
    }
  }, [menuData, activeCategory]);

  useEffect(() => {
    if (!user) return;

    // На /takeaway welcome-экран пропускаем:
    // это публичная страница самовывоза, гость не через QR зашёл,
    // onboarding ему мешает. Язык/тема подтянем из общих настроек или дефолтов.
    if (takeawayMode) {
      const savedLang = localStorage.getItem(`language_${user.uid}`) as Language | null;
      if (savedLang) setLanguage(savedLang);
      const savedTheme = localStorage.getItem(`theme_${user.uid}`) as Theme | null;
      if (savedTheme) setTheme(savedTheme);
      setShowWelcome(false);
      const timer = setTimeout(() => setIsLoading(false), 200);
      return () => clearTimeout(timer);
    }

    const onboarded = localStorage.getItem(`onboarded_${user.uid}`) || sessionStorage.getItem(`onboarded_${user.uid}`);
    if (onboarded) {
      const savedLang = localStorage.getItem(`language_${user.uid}`) as Language | null;
      if (savedLang) setLanguage(savedLang);

      const savedTheme = localStorage.getItem(`theme_${user.uid}`) as Theme | null;
      if (savedTheme) setTheme(savedTheme);

      setShowWelcome(false);
    } else {
      setShowWelcome(true);
    }

    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, [user, takeawayMode]);

  useScrollLock(!!selectedDish || isAiOpen || showWelcome);

  // Optimized Scroll Spy Logic
  useEffect(() => {
    const container = mainScrollRef.current;
    const handleScroll = () => {
      if (isScrollingRef.current) return;

      const scrollPosition = container?.scrollTop || 0;
      const offset = 100;

      let currentSection = menuData.length > 0 ? menuData[0].id : '';

      for (const category of menuData) {
        const element = sectionRefs.current[category.id];
        if (element) {
          if (element.offsetTop - offset <= scrollPosition) {
            currentSection = category.id;
          }
        }
      }
      setActiveCategory(currentSection);

      if (scrollPosition > 600) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, [menuData]);


  const handleLanguageChange = useCallback((lang: Language) => {
    setLanguage(lang);
    if (user) {
      localStorage.setItem(`language_${user.uid}`, lang);
    }
  }, [user]);

  const handleOnboardingComplete = (selectedLang: Language, selectedTheme: Theme) => {
    if (!user) return;
    setLanguage(selectedLang);
    setTheme(selectedTheme);
    localStorage.setItem(`language_${user.uid}`, selectedLang);
    localStorage.setItem(`theme_${user.uid}`, selectedTheme);
    localStorage.setItem(`onboarded_${user.uid}`, 'true');
    sessionStorage.setItem(`onboarded_${user.uid}`, 'true');
    setShowWelcome(false);
  };
  
  const handleRecommendation = useCallback((itemId: number) => {
    setRecommendedItemId(itemId);
    const item = menuData.flatMap(c => c.items).find(i => i.id === itemId);
    if(item) {
        setSelectedDish(item);
    }
    if (recommendationTimerRef.current) clearTimeout(recommendationTimerRef.current);
    recommendationTimerRef.current = setTimeout(() => setRecommendedItemId(null), 4000);
  }, [menuData]);
  
  const handleCategorySelect = (id: string) => {
      isScrollingRef.current = true;
      setActiveCategory(id);
      
      const element = sectionRefs.current[id];
      if (element) {
          const offset = 80;
          // rAF-скролл вместо native smooth: в части webview/встроенных браузеров
          // scrollTo({behavior:'smooth'}) — no-op, и прыжок к категории не работал.
          smoothScrollTo(mainScrollRef.current, element.offsetTop - offset);
      }
      
      if (scrollResetTimerRef.current) clearTimeout(scrollResetTimerRef.current);
      scrollResetTimerRef.current = setTimeout(() => { isScrollingRef.current = false; }, 800);
  };

  const handleVisualSearch = async (imageBase64: string) => {
    setIsAiLoading(true);
    try {
      const itemId = await findDishByImage(imageBase64, language);
      if(itemId) {
        handleRecommendation(itemId);
      } else {
        toast.error(t('visualSearch.notFound', language));
      }
    } catch (error) {
      console.error("Visual search failed:", error);
      toast.error(t('visualSearch.error', language));
    } finally {
      setIsAiLoading(false);
    }
  }

  const featuredItems = useMemo(() => {
    return menuData.flatMap(c => c.items).filter(i => i.isPopular).slice(0, 6);
  }, [menuData]);

  const filteredMenuData = useMemo(() => {
    const lowercasedQuery = searchQuery.toLowerCase();
    const filters = activeFilters;
    return menuData.map(category => ({
      ...category,
      items: category.items.filter(item => {
        // text-фильтр
        const name = item.names[language] || '';
        const description = item.descriptions[language] || '';
        const textOk = !lowercasedQuery
          || name.toLowerCase().includes(lowercasedQuery)
          || description.toLowerCase().includes(lowercasedQuery);
        if (!textOk) return false;
        // chip-фильтры. "popular" — это isPopular=true; остальные — теги в item.tags.
        if (filters.size === 0) return true;
        for (const f of filters) {
          if (f === 'popular') { if (!item.isPopular) return false; }
          else if (f === 'favourite') { if (!favs.has(item.id)) return false; }
          else { if (!item.tags?.includes(f as any)) return false; }
        }
        return true;
      })
    })).filter(category => category.items.length > 0);
  }, [searchQuery, language, menuData, activeFilters, favs]);

  const toggleFilter = (key: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div className="relative w-full h-full flex flex-col bg-[var(--bg-primary)] transition-colors duration-500 overflow-hidden">
      <AnimatePresence>
        {(isLoading || menuLoading) && !showWelcome && (
          <motion.div
            key="loading-screen"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[300] bg-[var(--bg-primary)] overflow-hidden"
          >
            <SkeletonLoading />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showWelcome && (
          <Welcome key="welcome-screen" onComplete={handleOnboardingComplete} />
        )}
      </AnimatePresence>
      
      <main 
        ref={mainScrollRef} 
        className="flex-1 overflow-y-auto no-scrollbar scroll-smooth pt-4 pb-40"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="max-w-xl md:max-w-4xl lg:max-w-6xl xl:max-w-7xl mx-auto px-5">
            {/* Header */}
            <div className="pt-5 pb-4 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, var(--accent-color) 0%, transparent 50%), radial-gradient(circle at 80% 20%, var(--accent-color) 0%, transparent 40%)'}}></div>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-[var(--accent-10)] rounded-2xl flex items-center justify-center">
                            <ValleyOfTasteLogo className="w-7 h-7 text-[var(--accent-color)]" />
                        </div>
                        <div>
                            <h1 className="font-serif font-bold text-lg text-[var(--text-primary)] leading-tight">
                              {settings.restaurantName[language] || settings.restaurantName.ru}
                            </h1>
                            <p className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                              {t('menuHeader.subtitle', language)}
                            </p>
                            {settings.workingHours && (() => {
                              const now = new Date();
                              const currentTime = now.getHours() * 60 + now.getMinutes();
                              const [openH, openM] = (settings.workingHours.open || '09:00').split(':').map(Number);
                              const [closeH, closeM] = (settings.workingHours.close || '22:00').split(':').map(Number);
                              const openTime = openH * 60 + openM;
                              const closeTime = closeH * 60 + closeM;
                              const isOpen = currentTime >= openTime && currentTime < closeTime;
                              return (
                                <div className={`flex items-center gap-1 mt-1 ${isOpen ? 'text-success-fg' : 'text-danger-fg'}`}>
                                  <div className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-success-bg-500 animate-pulse' : 'bg-danger'}`}></div>
                                  <span className="text-[10px] font-bold">
                                    {isOpen
                                      ? (language === 'ru' ? 'Открыто' : language === 'ky' ? 'Ачык' : language === 'zh' ? '营业中' : 'Open')
                                      : (language === 'ru' ? 'Закрыто' : language === 'ky' ? 'Жабык' : language === 'zh' ? '已关闭' : 'Closed')
                                    }
                                    {' · '}{settings.workingHours.open}–{settings.workingHours.close}
                                  </span>
                                </div>
                              );
                            })()}
                        </div>
                    </div>
                    {currentTable && (
                      <div className="bg-[var(--neutral-bg)] text-[var(--text-primary)] text-[11px] font-bold px-3 py-1.5 rounded-lg border border-[var(--glass-border)]">
                        {tableLabel(currentTable, language)}
                      </div>
                    )}
                </div>
            </div>

            <SearchHeader
              language={language}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onVisualSearch={handleVisualSearch}
              isAiLoading={isAiLoading}
            />

            {/* Quick-filter chips: tags из item.tags + isPopular. Скрываются
                когда активен поиск (текст имеет приоритет). */}
            {!searchQuery && (
              <div className="-mt-3 mb-4 flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {([
                  ...(favs.size > 0 ? [{ key: 'favourite' as const, emoji: '❤️', label: t('filters.favourite', language) }] : []),
                  { key: 'popular' as const, emoji: '⭐', label: t('filters.bestSeller', language) },
                  { key: 'vegetarian' as const, emoji: '🌱', label: t('filters.vegetarian', language) },
                  { key: 'spicy' as const, emoji: '🌶', label: t('filters.spicy', language) },
                  { key: 'gluten-free' as const, emoji: '🥒', label: t('filters.glutenFree', language) },
                ]).map((f) => {
                  const active = activeFilters.has(f.key);
                  return (
                    <button
                      key={f.key}
                      onClick={() => toggleFilter(f.key)}
                      className={`flex-shrink-0 px-3.5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${
                        active
                          ? 'bg-[var(--accent-color)] text-white shadow-md shadow-[var(--accent-15)]'
                          : 'bg-[var(--bg-card)] text-[var(--text-secondary)] border border-[var(--glass-border)] hover:border-[var(--accent-20)]'
                      }`}
                    >
                      <span className="mr-1">{f.emoji}</span>
                      {f.label}
                    </button>
                  );
                })}
                {activeFilters.size > 0 && (
                  <button
                    onClick={() => setActiveFilters(new Set())}
                    className="flex-shrink-0 px-3 py-1.5 text-xs font-bold text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                  >
                    × {t('filters.reset', language)}
                  </button>
                )}
              </div>
            )}

            {/* Promo-баннер "Скидки для сотрудников" убран из гостевого меню —
                он путал обычных посетителей. PIN-код персонал вводит в чекауте. */}

            {!searchQuery && (
              <FeaturedDishes
                items={featuredItems}
                language={language}
                onSelect={setSelectedDish}
              />
            )}

            {/* Карусель «Вы недавно смотрели» — показывается только если в
                localStorage уже накопилось ≥ 2 блюд. Сам компонент сам решает
                показываться или нет. */}
            {!searchQuery && activeFilters.size === 0 && (
              <RecentlyViewed language={language} onSelect={setSelectedDish} />
            )}
            
            {/* Липкий бар навигации по категориям со scroll-spy. Пиннится к
                верху при прокрутке в список секций. Сам скрывается, если
                категорий < 2 (напр. узкий поиск). */}
            <CategoryNav
              categories={filteredMenuData}
              activeCategory={activeCategory}
              onSelectCategory={handleCategorySelect}
              language={language}
            />

            <div className="mt-4 min-h-[60vh]">
            {filteredMenuData.length > 0 ? filteredMenuData.map((category) => (
              <MenuSection 
                key={category.id} 
                category={category} 
                language={language}
                recommendedItemId={recommendedItemId}
                onDishSelect={setSelectedDish}
                ref={el => { sectionRefs.current[category.id] = el; }}
              />
            )) : (
              <NoResults
                onClear={() => { setSearchQuery(''); }}
                onSuggest={(q) => setSearchQuery(q)}
                language={language}
              />
            )}
            
            <div className="h-24 w-full"></div>
          </div>

          {/* Футер только на /takeaway — для гостя в зале (через QR) он избыточен */}
          {takeawayMode && <Footer />}
        </div>

        <AnimatePresence>
          {showScrollTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => smoothScrollTo(mainScrollRef.current, 0)}
              className="fixed bottom-[180px] left-5 z-[40] w-10 h-10 bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-full shadow-lg flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>
      </main>

      <CartWidget
        language={language}
        tableNumber={currentTable}
        takeawayMode={takeawayMode}
      />

      <BottomDock
         currentLanguage={language}
         onLanguageChange={handleLanguageChange}
         currentTheme={theme}
         onThemeChange={toggleTheme}
         onAiClick={() => setIsAiOpen(true)}
         onSelectCategory={handleCategorySelect}
         tableNumber={currentTable}
      />
      
      <AiRecommender 
        isOpen={isAiOpen}
        onClose={() => setIsAiOpen(false)}
        language={language} 
        onRecommend={handleRecommendation} 
      />

      <DishDetailModal 
        dish={selectedDish} 
        onClose={() => setSelectedDish(null)} 
        language={language} 
      />
    </div>
  );
};

export default Home;
